import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";
import Logo from "@/components/Logo";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { t } = useLang();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("no_session"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") { setStatus("paid"); }
        else if (attempts >= 15) { setStatus("timeout"); }
        else { setAttempts((a) => a + 1); setTimeout(poll, 2000); }
      } catch { if (!cancelled) setStatus("error"); }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-[#FAF9F4] flex flex-col items-center justify-center p-6" data-testid="payment-success-page">
      <Logo size={28} />
      <div className="mt-12 max-w-md text-center">
        {status === "polling" && (<>
          <div className="w-12 h-12 border-2 border-[#B8933A] border-t-transparent rounded-full animate-spin mx-auto" />
          <h1 className="mt-6 font-[Fraunces] text-3xl">{t("confirming_payment")}</h1>
          <p className="mt-3 text-neutral-500 text-sm">{t("usually_seconds")}</p>
        </>)}
        {status === "paid" && (<>
          <CheckCircle2 className="w-14 h-14 text-[#B8933A] mx-auto" strokeWidth={1.5} />
          <h1 className="mt-6 font-[Fraunces] text-4xl">{t("welcome_upgraded")}</h1>
          <p className="mt-3 text-neutral-500">{t("account_upgraded")}</p>
          <Button onClick={() => nav("/app/projects")} className="mt-8 bg-black hover:bg-neutral-800 rounded-full px-6" data-testid="back-to-app-button">
            {t("go_to_dashboard")} →
          </Button>
        </>)}
        {status === "timeout" && (<>
          <h1 className="font-[Fraunces] text-3xl">{t("still_processing")}</h1>
          <p className="mt-3 text-neutral-500 text-sm">{t("stripe_delayed")}</p>
          <Button onClick={() => nav("/app/billing")} className="mt-6 rounded-full">{t("check_billing")}</Button>
        </>)}
        {(status === "no_session" || status === "error") && (<>
          <h1 className="font-[Fraunces] text-3xl">{t("didnt_work")}</h1>
          <p className="mt-3 text-neutral-500 text-sm">{t("payment_confirm_failed")}</p>
          <Button onClick={() => nav("/app/billing")} className="mt-6 rounded-full">{t("back_to_billing")}</Button>
        </>)}
      </div>
    </div>
  );
}
